class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    // Keep icon from navigating to /cart (fallback page is disabled in drawer mode).
    if (cartLink.getAttribute('href') === window.routes?.cart_url || cartLink.getAttribute('href') === '/cart') {
      cartLink.setAttribute('href', '#');
    }
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    try {
      if (sessionStorage.getItem('plOpenCartDrawer') === '1') {
        sessionStorage.removeItem('plOpenCartDrawer');
        setTimeout(() => this.open(cartLink), 50);
      }
    } catch (e) {}
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        try {
          if (typeof cartDrawerItems.updateCartUpsellToggleState === 'function') {
            cartDrawerItems.updateCartUpsellToggleState();
          }
          if (typeof cartDrawerItems.updateCartUpsellVisibility === 'function') {
            cartDrawerItems.updateCartUpsellVisibility();
          }
        } catch (err) {
          console.error(err);
        }
      }
      if (window.__plCartConfettiPending && typeof window.plCartCelebrateEmptyAdd === 'function') {
        window.__plCartConfettiPending = false;
        window.plCartCelebrateEmptyAdd();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    if (this._unlockTimer) clearTimeout(this._unlockTimer);
    this._unlockTimer = setTimeout(() => {
      if (!this.classList.contains('active')) {
        document.body.classList.remove('overflow-hidden');
      }
    }, 280);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      const html = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (html) sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getFastSectionsToRender() {
    return [];
  }

  formatMoney(cents, format) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(cents, format);
      } catch (e) {}
    }
    const value = (Number(cents) / 100).toFixed(2);
    return String(format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, value);
  }

  charm99(cents) {
    return Math.round((Number(cents) || 0) / 100) * 100;
  }

  ordinalSuffix(n) {
    n = Math.max(1, parseInt(n, 10) || 1);
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  displayUnitPrice(item) {
    const qty = Math.max(1, parseInt(item && item.quantity, 10) || 1);
    const unit = Number(item && item.final_price);
    const unitCents = Number.isFinite(unit)
      ? unit
      : Math.round((Number(item && item.final_line_price) || 0) / qty);
    return unitCents;
  }

  patchCartIconBubble(itemCount) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    itemCount = Math.max(0, parseInt(itemCount, 10) || 0);
    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (itemCount <= 0) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML =
        '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      cartLink.appendChild(bubble);
    }
    const visible = bubble.querySelector('[aria-hidden="true"]');
    const hidden = bubble.querySelector('.visually-hidden');
    if (visible) {
      if (itemCount < 100) {
        visible.textContent = String(itemCount);
        visible.hidden = false;
      } else {
        visible.textContent = '';
        visible.hidden = true;
      }
    }
    if (hidden) hidden.textContent = String(itemCount);
  }

  syncFromCart(parsedState) {
    if (!parsedState || !Array.isArray(parsedState.items)) return false;
    window.__plCart = parsedState;
    this.updateGiftBanner(parsedState);
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    parsedState.items.forEach((_, index) => {
      this.patchDrawerFromCart(parsedState, index + 1);
    });
    if (!parsedState.items.length) {
      this.patchDrawerFromCart(parsedState, 1);
    }
    this.updateProgressBar({
      total_price: parsedState.total_price,
      items_subtotal_price: parsedState.items_subtotal_price,
    });
    this.updateCartUpsellToggleState();
    this.updateCartUpsellVisibility(parsedState.item_count);
    return true;
  }

  updateGiftBanner(parsedState) {
    const drawer = document.querySelector('cart-drawer');
    const banner = drawer && drawer.querySelector('[data-pl-gift-banner]');
    if (!banner) return;

    let tagCount = 0;
    (parsedState.items || []).forEach((item) => {
      const handle = item.handle || (item.url || '').split('/products/')[1]?.split('?')[0] || '';
      if (handle === 'all-natural-flea-and-tick-tag') tagCount += Number(item.quantity) || 0;
    });
    banner.hidden = tagCount <= 0;
    const giftCount = banner.querySelector('[data-pl-gift-count]');
    if (giftCount) giftCount.textContent = 'FREE GIFTS INCLUDED';
    if (tagCount <= 0) return;

    const labels = [
      ['[data-pl-gift-capsules-label]', `${tagCount === 1 ? '3' : `${tagCount} x 3`} Refill Capsules`],
      ['[data-pl-gift-shipping-label]', `${tagCount} Free Shipping`],
      ['[data-pl-gift-collar-label]', `${tagCount} Adjustable ${tagCount === 1 ? 'Collar' : 'Collars'}`],
      ['[data-pl-gift-mystery-label]', '1 Mystery Gift'],
      ['[data-pl-gift-guide-label]', '1 Flea & Tick Guide'],
    ];
    labels.forEach(([selector, text]) => {
      const label = banner.querySelector(selector);
      if (label) label.textContent = text;
    });
  }

  patchDrawerFromCart(parsedState, line) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !parsedState || !Array.isArray(parsedState.items)) return false;
    this.updateGiftBanner(parsedState);

    const cartHeading = drawer.querySelector('[data-pl-cart-heading]');
    if (cartHeading) cartHeading.textContent = 'Your Cart';

    const format = drawer.getAttribute('data-money-format') || '${{amount}}';
    const money = (cents) => this.formatMoney(cents, format);
    const itemMoney = (cents) => money(cents);
    const unitCompare = (item, row) => {
      const fromRow = row ? Number(row.getAttribute('data-pl-compare-at')) || 0 : 0;
      const ajaxCompare = Number(item && item.compare_at_price) || 0;
      const original = Number(item && item.original_price) || 0;
      return Math.max(fromRow, ajaxCompare, original);
    };

    this.patchCartIconBubble(parsedState.item_count);

    let tagCount = 0;
    parsedState.items.forEach((it) => {
      const handle = it.handle || (it.url || '').split('/products/')[1]?.split('?')[0] || '';
      if (handle === 'all-natural-flea-and-tick-tag') tagCount += Number(it.quantity) || 0;
    });
    const addMore = drawer.querySelector('[data-pl-addmore]');
    const tier2Price = addMore ? Number(addMore.getAttribute('data-tier-2')) || 0 : 0;
    const tier3Price = addMore ? Number(addMore.getAttribute('data-tier-3')) || 0 : 0;

    parsedState.items.forEach((item, index) => {
      const itemLine = index + 1;
      const qtyInput = document.getElementById(`Drawer-quantity-${itemLine}`);
      if (qtyInput) {
        qtyInput.value = item.quantity;
        qtyInput.setAttribute('value', String(item.quantity));
      }
      const row = document.getElementById(`CartDrawer-Item-${itemLine}`);
      if (row) {
        const priceEl = row.querySelector('.pl-cart-item-price');
        const handle = item.handle || (item.url || '').split('/products/')[1]?.split('?')[0] || '';
        let displayUnit = this.displayUnitPrice(item);
        if (handle === 'all-natural-flea-and-tick-tag') {
          const rowTier1Price = Number(row.getAttribute('data-pl-tier-1-price')) || (addMore ? Number(addMore.getAttribute('data-base-price')) || 0 : 0);
          const rowTier2Price = Number(row.getAttribute('data-pl-tier-2-price')) || tier2Price;
          const rowTier3Price = Number(row.getAttribute('data-pl-tier-3-price')) || tier3Price;
          if (tagCount >= 3 && rowTier3Price > 0) displayUnit = rowTier3Price;
          else if (tagCount >= 2 && rowTier2Price > 0) displayUnit = rowTier2Price;
          else if (tagCount === 1 && rowTier1Price > 0) displayUnit = rowTier1Price;
        }
        if (priceEl) priceEl.textContent = itemMoney(displayUnit);
        const prices = row.querySelector('.pl-cart-item-prices');
        const quantityEl = row.querySelector('[data-pl-item-qty]');
        if (quantityEl) {
          quantityEl.textContent = `(x${item.quantity})`;
          quantityEl.hidden = item.quantity <= 1;
          if (prices && priceEl && quantityEl.parentNode === prices && priceEl.nextElementSibling !== quantityEl) {
            prices.insertBefore(quantityEl, priceEl.nextSibling);
          }
        }
        const monthlyPriceEl = row.querySelector('.pl-cart-item-month');
        if (monthlyPriceEl) monthlyPriceEl.textContent = `· Only ${money(Math.floor(displayUnit / 12))}/month`;
        const compareCents = unitCompare(item, row);
        const lineCompare = compareCents * item.quantity;
        const lineDisplay = displayUnit * item.quantity;
        let compareEl = row.querySelector('.pl-cart-item-compare');
        if (lineCompare > lineDisplay) {
          if (!compareEl && prices) {
            compareEl = document.createElement('s');
            compareEl.className = 'pl-cart-item-compare';
            prices.appendChild(compareEl);
          }
          if (compareEl) {
            compareEl.textContent = itemMoney(lineCompare);
            if (prices && compareEl.parentNode === prices) {
              prices.appendChild(compareEl);
            }
          }
        } else if (compareEl) {
          compareEl.remove();
        }
      }
    });

    let compareTotal = 0;
    parsedState.items.forEach((it, index) => {
      const row = document.getElementById(`CartDrawer-Item-${index + 1}`);
      compareTotal += unitCompare(it, row) * it.quantity;
    });
    const saveRow = drawer.querySelector('[data-pl-summary-save]') || drawer.querySelector('.pl-cart-summary__row--save');
    const save = Math.max(0, compareTotal - parsedState.total_price);
    if (saveRow) {
      saveRow.hidden = save <= 0;
      saveRow.style.display = save <= 0 ? 'none' : '';
      const saveValue = saveRow.querySelector('.pl-cart-summary__save-value');
      if (saveValue && save > 0) saveValue.textContent = `−${money(save)}`;
    }
    const discountRow = drawer.querySelector('[data-pl-summary-discount]');
    if (discountRow) {
      const discount = save;
      discountRow.hidden = discount <= 0;
      discountRow.style.display = discount <= 0 ? 'none' : '';
      const discountValue = discountRow.querySelector('.pl-cart-summary__discount-value');
      if (discountValue && discount > 0) discountValue.textContent = `-${money(discount)}`;
    }
    const totalRow = drawer.querySelector('.pl-cart-summary__row--total');
    const totalValue = totalRow && totalRow.querySelector('.pl-cart-summary__total');
    if (totalValue) totalValue.textContent = money(parsedState.total_price);
    const totalLabel = totalRow && totalRow.querySelector('[data-pl-cart-total-label]');
    if (totalLabel) totalLabel.textContent = 'Total';
    const compareValue = totalRow && totalRow.querySelector('[data-pl-summary-compare]');
    if (compareValue) {
      compareValue.textContent = money(compareTotal);
      compareValue.hidden = compareTotal <= parsedState.total_price;
    }
    const checkoutBtn = drawer.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) {
      const checkoutLabel = checkoutBtn.querySelector('.pl-cart-checkout__label');
      if (checkoutLabel) checkoutLabel.textContent = 'Secure Checkout';
      const checkoutTotal = checkoutBtn.querySelector('[data-pl-checkout-total]');
      if (checkoutTotal) checkoutTotal.textContent = money(parsedState.total_price);
    }

    if (addMore) {
      const shouldHideAddMore = tagCount < 1 || tagCount >= 3;
      addMore.hidden = shouldHideAddMore;
      addMore.style.display = shouldHideAddMore ? 'none' : '';
      const base = parseInt(addMore.getAttribute('data-base-price'), 10) || 0;
      const tier2 = parseInt(addMore.getAttribute('data-tier-2'), 10) || 0;
      const tier3 = parseInt(addMore.getAttribute('data-tier-3'), 10) || 0;
      addMore.setAttribute('data-tag-count', String(tagCount));
      const next = tagCount + 1;
      const title = addMore.querySelector('[data-pl-addmore-title]');
      if (base > 0) {
        let unit = base;
        if (next >= 3) unit = tier3 || Math.round((base * 85) / 100);
        else if (next >= 2) unit = tier2 || Math.round((base * 90) / 100);
        if (title) title.textContent = `Add a ${next}${this.ordinalSuffix(next)} tag — now ${money(unit)} each`;
      } else if (title) {
        title.textContent = `Add a ${next}${this.ordinalSuffix(next)} tag`;
      }
    }

    return true;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(function initPlCartAddMore() {
  if (window.__plCartAddMoreBound) return;
  window.__plCartAddMoreBound = true;

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.pl-cart-addmore__swatch');
    if (swatch && !swatch.disabled) {
      const root = swatch.closest('[data-pl-addmore]');
      if (!root) return;
      root.querySelectorAll('.pl-cart-addmore__swatch').forEach((btn) => {
        const on = btn === swatch;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      return;
    }

    const promoBtn = event.target.closest('[data-pl-promo-atc]');
    if (promoBtn) {
      const promoRoot = promoBtn.closest('[data-pl-cart-promo]');
      if (!promoRoot || promoBtn.disabled) return;

      const promoVariantId = promoRoot.getAttribute('data-variant-id');
      if (!promoVariantId) return;

      const promoCartItems = document.querySelector('cart-drawer-items');
      if (!promoCartItems || typeof promoCartItems.addUpsellProduct !== 'function') return;

      promoBtn.disabled = true;
      promoBtn.classList.add('loading');
      const promoSpinner = promoBtn.querySelector('.loading__spinner');
      if (promoSpinner) promoSpinner.classList.remove('hidden');

      promoCartItems
        .addUpsellProduct(promoVariantId)
        .catch((err) => {
          console.error(err);
          promoBtn.disabled = false;
          promoBtn.classList.remove('loading');
          if (promoSpinner) promoSpinner.classList.add('hidden');
        });
      return;
    }

    const addBtn = event.target.closest('[data-pl-addmore-btn]');
    if (!addBtn) return;

    const root = addBtn.closest('[data-pl-addmore]');
    if (!root || addBtn.disabled) return;

    const selected =
      root.querySelector('.pl-cart-addmore__swatch.is-selected') ||
      root.querySelector('.pl-cart-addmore__swatch:not([disabled])');
    const variantId = selected && selected.getAttribute('data-variant-id');
    if (!variantId) return;

    const cartItems = document.querySelector('cart-drawer-items');
    if (!cartItems || typeof cartItems.addUpsellProduct !== 'function') return;

    addBtn.disabled = true;
    addBtn.classList.add('loading');
    const spinner = addBtn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    cartItems
      .addUpsellProduct(variantId)
      .catch((err) => {
        console.error(err);
        addBtn.disabled = false;
        addBtn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      });
  });
})();
